import React from 'react';
import Flexbox from 'flexbox-react';
import { inject, observer } from 'mobx-react';

import Avatar from 'react-avatar';
import {
  Button,
  AnchorButton,
  Switch,
  Tooltip,
  Tabs,
  Tab,
} from '@blueprintjs/core';
import axios from 'axios';
import Cookies from 'js-cookie';

import ToastStore from '../../stores/ToastStore';
import GoalSettingsCreateForm from './GoalSettingsCreateForm';
import { Checkbox } from '@blueprintjs/core';
import { FilledButton } from '../buttons-20250224T111359Z-001/buttons';
import PageHeading from './PageHeading';
import GoalReportPage from './ReportGoalPage';

import { toJS } from 'mobx';
import PropTypes from 'prop-types';
import BlankPage from './BlankPage';

const GOAL_TABLE_STYLES = `
.table-row {
line-height: 24px;
transition: background-color 0.2s ease-in-out;
}
.table-row:hover {
background-color: #f5f5f5;
}
.status-row {
align-items: center;
text-align: center;
justify-content: center;
} 
.status-row span {
padding: 8px 20px;
border-radius: 50px;
text-align: center;
} 
.title-row {
text-decoration: none;
}
.ellipsis-text {
max-width: 250px; 
white-space: nowrap;
overflow: hidden;
text-overflow: ellipsis;
display: inline-block;
} 
table.bp3-html-table thead th.table-header-text, 
tbody td.table-header-text,
tbody td.span.table-header-text {
color: #4D4D4D;
font-family: Roboto;
font-size: 16px;
font-style: normal;
font-weight: 400;
line-height: normal;
}
table-header-text {
color: #4D4D4D;
font-family: Roboto;
font-size: 18px;
font-style: normal;
font-weight: 400;
line-height: normal;
}
.bp3-tabs {
margin-bottom: 20px;
}
.bp3-tab-list {
border-bottom: 1px solid #e5e5e5;
}
.bp3-tab {
color: #4D4D4D;
font-family: Roboto;
font-size:16px;
border-bottom: 2px solid transparent;
transition: all 0.2s ease-in-out;
}
.bp3-tab:hover {
color: #5C6BC0;
}
.bp3-tab[aria-selected="true"] {
color: #5C6BC0;
border-bottom-color: #5C6BC0;
font-weight: 500;
}
.bp3-tab-indicator-wrapper {
display: none;
}
.goal-table-container {
width: 100%;
overflow-x: auto;
}
.goal-table-container table {
width: 100%;
border-collapse: collapse;
}
`;

@inject('ToastStore', 'CurrentUserStore')
@observer
export default class GoalsFormList extends React.Component {
  state = {
    selectedGoal: null,
    selectedGoals: [],
    reportGoal: null,
    activeTab: 'published',
  };

  componentDidMount() {
    const { error } = this.props;
    if (error) {
      ToastStore.showToast(error, 'danger');
    }
  }

  getGoalsArray = () => {
    const { goals } = this.props;

    let goalsArray = [];

    if (Array.isArray(goals)) {
      goalsArray = goals;
    } else {
      try {
        const maybePlain =
          typeof toJS === 'function' ? toJS(goals) : goals;
        if (Array.isArray(maybePlain)) {
          goalsArray = maybePlain;
        }
      } catch (err) {
        console.error('error', err);
      }

      if (!goalsArray.length && goals && Array.isArray(goals.data)) {
        goalsArray = goals.data;
      }
      if (!goalsArray.length && goals && Array.isArray(goals.items)) {
        goalsArray = goals.items;
      }
    }

    return goalsArray;
  };

  getFilteredGoalsArray = () => {
    const { activeTab } = this.state;
    const goalsArray = this.getGoalsArray();

    if (activeTab === 'published') {
      return goalsArray.filter(goal => goal && goal.status === 1);
    } else if (activeTab === 'draft') {
      return goalsArray.filter(goal => goal && goal.status === 0);
    } else if (activeTab === 'archived') {
      return goalsArray.filter(goal => goal && goal.status === 2);
    }

    return goalsArray;
  };

  handleCheckboxChange = goalId => {
    this.setState(prevState => {
      const { selectedGoals } = prevState;
      return {
        selectedGoals: selectedGoals.includes(goalId)
          ? selectedGoals?.filter(id => id !== goalId)
          : [...selectedGoals, goalId],
      };
    });
  };

  getSelectableGoalIds = () => {
    const filteredGoals = this.getFilteredGoalsArray();
    // Only selectable goals are Draft (0) and Published (1), not Archived (2)
    return filteredGoals
      .filter(
        goal => goal && (goal?.status === 0 || goal?.status === 1)
      )
      .map(goal => goal?.id);
  };

  handleTabChange = newTabId => {
    this.setState({
      activeTab: newTabId,
      selectedGoals: [], // Clear selections when switching tabs
    });
  };

  handleSelectAll = () => {
    const { selectedGoals } = this.state;
    const selectableIds = this.getSelectableGoalIds();

    this.setState({
      selectedGoals:
        selectedGoals.length === selectableIds.length
          ? []
          : selectableIds,
    });
  };

  handleEdit = goal => {
    this.setState({ selectedGoal: goal });
  };

  handleReportGoal = goal => {
    this.setState({ reportGoal: goal });
  };

  handleCloseForm = () => {
    this.setState({ selectedGoal: null, reportGoal: null });
  };

  handleDelete = () => {
    const { selectedGoals } = this.state;
    const { getAllGoals } = this.props;

    if (selectedGoals.length === 0) {
      alert('No goals selected!');
      return;
    }

    axios
      .post(`${Cookies.get('apiEnv')}/goal_settings/update_status`, {
        ids: selectedGoals,
        status: 2,
      })
      .then(response => {
        alert('Goals deleted successfully!');
        this.setState({ selectedGoals: [] });
        getAllGoals();
      })
      .catch(error => {
        console.error('Error deleting goals:', error);
      });
  };

  render() {
    const {
      selectedGoals,
      selectedGoal,
      reportGoal,
      activeTab,
    } = this.state;
    const {
      onAddGoal,
      goals,
      loading,
      getAllGoals,
      CurrentUserStore,
    } = this.props;

    const goalsArray = this.getGoalsArray();
    const filteredGoalsArray = this.getFilteredGoalsArray();

    const canCreate =
      CurrentUserStore?.hasAnyModulePermission?.('goals', [
        'write',
        'create',
      ]) || false;
    const canEdit =
      CurrentUserStore?.hasAnyModulePermission?.('goals', [
        'write',
        'edit',
      ]) || false;
    const canDelete =
      CurrentUserStore?.hasAnyModulePermission?.('goals', [
        'write',
        'delete',
      ]) || false;
    const isReadOnly =
      CurrentUserStore?.isModuleReadOnly?.('goals') || false;

    // const canClickRow = !isReadOnly && (canEdit || canCreate);
    const canClickRow =
      !isReadOnly &&
      (canEdit || canCreate) &&
      activeTab !== 'archived';

    const selectableGoalIds = this.getSelectableGoalIds();
    const allSelected =
      selectableGoalIds.length > 0 &&
      selectableGoalIds.every(id => selectedGoals.includes(id));

    // Count goals for each tab
    const publishedCount = goalsArray.filter(
      goal => goal && goal.status === 1
    ).length;
    const draftCount = goalsArray.filter(
      goal => goal && goal.status === 0
    ).length;
    const archivedCount = goalsArray.filter(
      goal => goal && goal.status === 2
    ).length;

    return (
      <Flexbox flexDirection="column" flexGrow={1}>
        <style>{GOAL_TABLE_STYLES}</style>
        {!loading ? (
          <>
            {!selectedGoal && !reportGoal && (
              <PageHeading
                text="Goal Cycle"
                description="Launch new goal cycles and keep their track by reviewing their individual reports."
              />
            )}
            {!selectedGoal &&
              !reportGoal &&
              canCreate &&
              !isReadOnly && (
                <Flexbox
                  flexDirection="row"
                  justifyContent="flex-end"
                  marginBottom="20px"
                >
                  <FilledButton
                    text="+ Add Goal Cycle"
                    onClick={onAddGoal}
                  />
                </Flexbox>
              )}
            {selectedGoal ? (
              <GoalSettingsCreateForm
                goalData={selectedGoal}
                onClose={this.handleCloseForm}
                getAllGoals={getAllGoals}
              />
            ) : reportGoal ? (
              <GoalReportPage
                goalData={reportGoal}
                onClose={this.handleCloseForm}
              />
            ) : (
              <>
                <Tabs
                  id="goal-tabs"
                  selectedTabId={activeTab}
                  onChange={this.handleTabChange}
                  large={false}
                >
                  <Tab
                    id="published"
                    title={`Published (${publishedCount})`}
                    panel={
                      <div className="goal-table-container">
                        {this.getFilteredGoalsArray().length > 0 ? (
                          <table
                            className="bp3-html-table bp3-html-table-bordered bp3-small"
                            style={{
                              border: '1px solid #e5e5e5',
                              width: '100%',
                            }}
                          >
                            <thead
                              style={{
                                backgroundColor:
                                  'rgba(189, 195, 199, 0.10)',
                                color: '#4D4D4D',
                              }}
                            >
                              <tr
                                style={{
                                  alignContent: 'center',
                                  fontSize: '18px',
                                  fontWeight: '400',
                                  lineHeight: 'normal',
                                }}
                              >
                                {!isReadOnly && canDelete && (
                                  <th
                                    className="table-header-text"
                                    style={{
                                      width: '8%',
                                      padding: '8px 12px',
                                    }}
                                  >
                                    <Flexbox
                                      flexDirection="row"
                                      alignItems="center"
                                    >
                                      <Checkbox
                                        onChange={
                                          this.handleSelectAll
                                        }
                                        checked={allSelected}
                                        style={{
                                          marginBottom: '0px',
                                        }}
                                      />
                                      <Button
                                        onClick={this.handleDelete}
                                        icon="trash"
                                        className="bp3-small bp3-minimal"
                                        disabled={
                                          selectedGoals.length === 0
                                        }
                                        style={{
                                          visibility:
                                            selectedGoals.length > 0
                                              ? 'visible'
                                              : 'hidden',
                                        }}
                                      />
                                    </Flexbox>
                                  </th>
                                )}
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '27%',
                                    alignContent: 'center',
                                    padding:
                                      canDelete && !isReadOnly
                                        ? undefined
                                        : '12px 12px',
                                  }}
                                >
                                  Name
                                </th>
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '20%',
                                    alignContent: 'center',
                                  }}
                                >
                                  Created By
                                </th>
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '15%',
                                    textAlign: 'center',
                                    alignContent: 'center',
                                  }}
                                >
                                  Created On
                                </th>
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '15%',
                                    textAlign: 'center',
                                    alignContent: 'center',
                                  }}
                                >
                                  Status
                                </th>
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '20%',
                                    textAlign: 'center',
                                    alignContent: 'center',
                                  }}
                                >
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {this.getFilteredGoalsArray().map(
                                (goal, index) => {
                                  const canSelectGoal =
                                    goal.status === 0 ||
                                    goal.status === 1;
                                  const rowCursor = canClickRow
                                    ? 'pointer'
                                    : 'default';
                                  return (
                                    <tr
                                      className="table-row"
                                      key={goal.id || index}
                                      style={{
                                        cursor: rowCursor,
                                        alignContent: 'center',
                                        height: '65px',
                                        verticalAlign: 'middle',
                                      }}
                                    >
                                      {!isReadOnly && canDelete && (
                                        <td
                                          style={{
                                            verticalAlign: 'middle',
                                          }}
                                        >
                                          {canSelectGoal && (
                                            <Checkbox
                                              id={goal.id}
                                              onChange={() =>
                                                this.handleCheckboxChange(
                                                  goal.id
                                                )
                                              }
                                              checked={selectedGoals.includes(
                                                goal.id
                                              )}
                                              style={{
                                                marginBottom: '0px',
                                              }}
                                            />
                                          )}
                                        </td>
                                      )}
                                      <td
                                        className="title-row table-header-text"
                                        onClick={
                                          canClickRow
                                            ? () =>
                                                this.handleEdit(goal)
                                            : undefined
                                        }
                                        style={{
                                          width: '27%',
                                          verticalAlign: 'middle',
                                        }}
                                      >
                                        <Tooltip
                                          content={goal.title}
                                          position="top"
                                        >
                                          <span className="ellipsis-text">
                                            {goal.title}
                                          </span>
                                        </Tooltip>
                                      </td>
                                      <td
                                        className="table-header-text"
                                        style={{
                                          verticalAlign: 'middle',
                                        }}
                                      >
                                        <Flexbox
                                          flexDirection="row"
                                          flexGrow={0}
                                          className="bp3-cursor-pointer table-header-text"
                                        >
                                          <Flexbox
                                            flexDirection="row"
                                            alignItems="center"
                                          >
                                            <Avatar
                                              src={
                                                goal?.user
                                                  ?.imageFileName
                                              }
                                              size={22}
                                              round={true}
                                              name={
                                                goal.user.fullName
                                              }
                                              className="push-5-r"
                                            />
                                          </Flexbox>
                                          <Flexbox
                                            className="table-header-text"
                                            flexDirection="row"
                                            alignItems="center"
                                          >
                                            <span className="table-header-text">
                                              {goal?.user?.fullName}
                                            </span>
                                          </Flexbox>
                                        </Flexbox>
                                      </td>
                                      <td
                                        className="table-header-text"
                                        style={{
                                          textAlign: 'center',
                                          verticalAlign: 'middle',
                                        }}
                                      >
                                        {new Date(
                                          goal.created_at
                                        ).toLocaleDateString(
                                          'en-US',
                                          {
                                            year: 'numeric',
                                            month: 'short',
                                            day: '2-digit',
                                          }
                                        )}
                                      </td>
                                      <td
                                        style={{
                                          textAlign: 'center',
                                          verticalAlign: 'middle',
                                        }}
                                        className="status-row table-header-text"
                                      >
                                        {goal.status === 0 ? (
                                          <span
                                            style={{
                                              border:
                                                '1px solid #707070',
                                              textAlign: 'center',
                                              color: '#707070',
                                            }}
                                          >
                                            Draft
                                          </span>
                                        ) : goal.status === 1 ? (
                                          <span
                                            style={{
                                              border:
                                                '1px solid #10A423',
                                              textAlign: 'center',
                                              color: '#10A423',
                                            }}
                                          >
                                            Published
                                          </span>
                                        ) : (
                                          <span
                                            style={{
                                              border:
                                                '1px solid #D99B00',
                                              textAlign: 'center',
                                              color: '#D99B00',
                                            }}
                                          >
                                            Archived
                                          </span>
                                        )}
                                      </td>
                                      <td
                                        style={{
                                          textAlign: 'center',
                                          verticalAlign: 'middle',
                                        }}
                                        className="status-row table-header-text"
                                      >
                                        <Flexbox
                                          flexDirection="row"
                                          justifyContent="center"
                                        >
                                          {!isReadOnly &&
                                            canEdit &&
                                            goal.status !== 2 && (
                                              <Button
                                                icon={
                                                  <svg
                                                    width="21"
                                                    height="21"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                    <path
                                                      d="M7.63704 21.2615H2.51904L2.69554 16.1885L16.1595 3.1285C16.4168 2.87888 16.761 2.73903 17.1194 2.73847C17.4779 2.73791 17.8225 2.87669 18.0805 3.1255L21.059 5.9975C21.1916 6.12538 21.2973 6.27848 21.3698 6.44778C21.4424 6.61708 21.4803 6.79918 21.4815 6.98337C21.4827 7.16756 21.447 7.35012 21.3766 7.52033C21.3062 7.69053 21.2025 7.84495 21.0715 7.9745L7.63704 21.2615ZM3.47254 20.3385H7.25854L20.4235 7.3195C20.4672 7.27634 20.5018 7.22488 20.5253 7.16815C20.5488 7.11142 20.5607 7.05056 20.5603 6.98916C20.56 6.92776 20.5473 6.86705 20.5231 6.81061C20.499 6.75416 20.4637 6.70313 20.4195 6.6605L17.441 3.7885C17.3552 3.70538 17.2403 3.65891 17.1208 3.65891C17.0013 3.65891 16.8864 3.70538 16.8005 3.7885L3.60054 16.5885L3.47254 20.3385Z"
                                                      fill="#707070"
                                                    />
                                                    <path
                                                      d="M18.8589 9.5245L14.4719 5.21L15.2029 4.491L19.5899 8.8055L18.8589 9.5245Z"
                                                      fill="#707070"
                                                    />
                                                  </svg>
                                                }
                                                minimal
                                                onClick={() =>
                                                  this.handleEdit(
                                                    goal
                                                  )
                                                }
                                                className="no-hover-btn"
                                              />
                                            )}
                                        </Flexbox>
                                      </td>
                                    </tr>
                                  );
                                }
                              )}
                            </tbody>
                          </table>
                        ) : (
                          <div className="push-30-t">
                            <BlankPage
                              title={'No Published Goal Cycle Found'}
                              description={`You haven't created any published goal cycle yet.`}
                            />
                          </div>
                        )}
                      </div>
                    }
                  />
                  <Tab
                    id="draft"
                    title={`Draft (${draftCount})`}
                    panel={
                      <div className="goal-table-container">
                        {this.getFilteredGoalsArray().length > 0 ? (
                          <table
                            className="bp3-html-table bp3-html-table-bordered bp3-small"
                            style={{
                              border: '1px solid #e5e5e5',
                              width: '100%',
                            }}
                          >
                            <thead
                              style={{
                                backgroundColor:
                                  'rgba(189, 195, 199, 0.10)',
                                color: '#4D4D4D',
                              }}
                            >
                              <tr
                                style={{
                                  alignContent: 'center',
                                  fontSize: '18px',
                                  fontWeight: '400',
                                  lineHeight: 'normal',
                                }}
                              >
                                {!isReadOnly && canDelete && (
                                  <th
                                    className="table-header-text"
                                    style={{
                                      width: '8%',
                                      padding: '8px 12px',
                                    }}
                                  >
                                    <Flexbox
                                      flexDirection="row"
                                      alignItems="center"
                                    >
                                      <Checkbox
                                        onChange={
                                          this.handleSelectAll
                                        }
                                        checked={allSelected}
                                        style={{
                                          marginBottom: '0px',
                                        }}
                                      />
                                      <Button
                                        onClick={this.handleDelete}
                                        icon="trash"
                                        className="bp3-small bp3-minimal"
                                        disabled={
                                          selectedGoals.length === 0
                                        }
                                        style={{
                                          visibility:
                                            selectedGoals.length > 0
                                              ? 'visible'
                                              : 'hidden',
                                        }}
                                      />
                                    </Flexbox>
                                  </th>
                                )}
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '27%',
                                    alignContent: 'center',
                                    padding:
                                      canDelete && !isReadOnly
                                        ? undefined
                                        : '12px 12px',
                                  }}
                                >
                                  Name
                                </th>
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '20%',
                                    alignContent: 'center',
                                  }}
                                >
                                  Created By
                                </th>
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '15%',
                                    textAlign: 'center',
                                    alignContent: 'center',
                                  }}
                                >
                                  Created On
                                </th>
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '15%',
                                    textAlign: 'center',
                                    alignContent: 'center',
                                  }}
                                >
                                  Status
                                </th>
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '20%',
                                    textAlign: 'center',
                                    alignContent: 'center',
                                  }}
                                >
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {this.getFilteredGoalsArray().map(
                                (goal, index) => {
                                  const canSelectGoal =
                                    goal.status === 0 ||
                                    goal.status === 1;
                                  const rowCursor = canClickRow
                                    ? 'pointer'
                                    : 'default';
                                  return (
                                    <tr
                                      className="table-row"
                                      key={goal.id || index}
                                      style={{
                                        cursor: rowCursor,
                                        alignContent: 'center',
                                        height: '65px',
                                        verticalAlign: 'middle',
                                      }}
                                    >
                                      {!isReadOnly && canDelete && (
                                        <td
                                          style={{
                                            verticalAlign: 'middle',
                                          }}
                                        >
                                          {canSelectGoal && (
                                            <Checkbox
                                              id={goal.id}
                                              onChange={() =>
                                                this.handleCheckboxChange(
                                                  goal.id
                                                )
                                              }
                                              checked={selectedGoals.includes(
                                                goal.id
                                              )}
                                              style={{
                                                marginBottom: '0px',
                                              }}
                                            />
                                          )}
                                        </td>
                                      )}
                                      <td
                                        className="title-row table-header-text"
                                        onClick={
                                          canClickRow
                                            ? () =>
                                                this.handleEdit(goal)
                                            : undefined
                                        }
                                        style={{
                                          width: '27%',
                                          verticalAlign: 'middle',
                                        }}
                                      >
                                        <Tooltip
                                          content={goal.title}
                                          position="top"
                                        >
                                          <span className="ellipsis-text">
                                            {goal.title}
                                          </span>
                                        </Tooltip>
                                      </td>
                                      <td
                                        className="table-header-text"
                                        style={{
                                          verticalAlign: 'middle',
                                        }}
                                      >
                                        <Flexbox
                                          flexDirection="row"
                                          flexGrow={0}
                                          className="bp3-cursor-pointer table-header-text"
                                        >
                                          <Flexbox
                                            flexDirection="row"
                                            alignItems="center"
                                          >
                                            <Avatar
                                              src={
                                                goal?.user
                                                  ?.imageFileName
                                              }
                                              size={22}
                                              round={true}
                                              name={
                                                goal.user.fullName
                                              }
                                              className="push-5-r"
                                            />
                                          </Flexbox>
                                          <Flexbox
                                            className="table-header-text"
                                            flexDirection="row"
                                            alignItems="center"
                                          >
                                            <span className="table-header-text">
                                              {goal?.user?.fullName}
                                            </span>
                                          </Flexbox>
                                        </Flexbox>
                                      </td>
                                      <td
                                        className="table-header-text"
                                        style={{
                                          textAlign: 'center',
                                          verticalAlign: 'middle',
                                        }}
                                      >
                                        {new Date(
                                          goal.created_at
                                        ).toLocaleDateString(
                                          'en-US',
                                          {
                                            year: 'numeric',
                                            month: 'short',
                                            day: '2-digit',
                                          }
                                        )}
                                      </td>
                                      <td
                                        style={{
                                          textAlign: 'center',
                                          verticalAlign: 'middle',
                                        }}
                                        className="status-row table-header-text"
                                      >
                                        {goal.status === 0 ? (
                                          <span
                                            style={{
                                              border:
                                                '1px solid #707070',
                                              textAlign: 'center',
                                              color: '#707070',
                                            }}
                                          >
                                            Draft
                                          </span>
                                        ) : goal.status === 1 ? (
                                          <span
                                            style={{
                                              border:
                                                '1px solid #10A423',
                                              textAlign: 'center',
                                              color: '#10A423',
                                            }}
                                          >
                                            Published
                                          </span>
                                        ) : (
                                          <span
                                            style={{
                                              border:
                                                '1px solid #D99B00',
                                              textAlign: 'center',
                                              color: '#D99B00',
                                            }}
                                          >
                                            Archived
                                          </span>
                                        )}
                                      </td>
                                      <td
                                        style={{
                                          textAlign: 'center',
                                          verticalAlign: 'middle',
                                        }}
                                        className="status-row table-header-text"
                                      >
                                        <Flexbox
                                          flexDirection="row"
                                          justifyContent="center"
                                        >
                                          {!isReadOnly &&
                                            canEdit &&
                                            goal.status !== 2 && (
                                              <Button
                                                icon={
                                                  <svg
                                                    width="21"
                                                    height="21"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                    <path
                                                      d="M7.63704 21.2615H2.51904L2.69554 16.1885L16.1595 3.1285C16.4168 2.87888 16.761 2.73903 17.1194 2.73847C17.4779 2.73791 17.8225 2.87669 18.0805 3.1255L21.059 5.9975C21.1916 6.12538 21.2973 6.27848 21.3698 6.44778C21.4424 6.61708 21.4803 6.79918 21.4815 6.98337C21.4827 7.16756 21.447 7.35012 21.3766 7.52033C21.3062 7.69053 21.2025 7.84495 21.0715 7.9745L7.63704 21.2615ZM3.47254 20.3385H7.25854L20.4235 7.3195C20.4672 7.27634 20.5018 7.22488 20.5253 7.16815C20.5488 7.11142 20.5607 7.05056 20.5603 6.98916C20.56 6.92776 20.5473 6.86705 20.5231 6.81061C20.499 6.75416 20.4637 6.70313 20.4195 6.6605L17.441 3.7885C17.3552 3.70538 17.2403 3.65891 17.1208 3.65891C17.0013 3.65891 16.8864 3.70538 16.8005 3.7885L3.60054 16.5885L3.47254 20.3385Z"
                                                      fill="#707070"
                                                    />
                                                    <path
                                                      d="M18.8589 9.5245L14.4719 5.21L15.2029 4.491L19.5899 8.8055L18.8589 9.5245Z"
                                                      fill="#707070"
                                                    />
                                                  </svg>
                                                }
                                                minimal
                                                onClick={() =>
                                                  this.handleEdit(
                                                    goal
                                                  )
                                                }
                                                className="no-hover-btn"
                                              />
                                            )}
                                        </Flexbox>
                                      </td>
                                    </tr>
                                  );
                                }
                              )}
                            </tbody>
                          </table>
                        ) : (
                          <div className="push-30-t">
                            <BlankPage
                              title={'No Draft Goal Cycle Found'}
                              description={`You don't have any draft goal cycles.`}
                            />
                          </div>
                        )}
                      </div>
                    }
                  />
                  <Tab
                    id="archived"
                    title={`Archived (${archivedCount})`}
                    panel={
                      <div className="goal-table-container">
                        {this.getFilteredGoalsArray().length > 0 ? (
                          <table
                            className="bp3-html-table bp3-html-table-bordered bp3-small"
                            style={{
                              border: '1px solid #e5e5e5',
                              width: '100%',
                            }}
                          >
                            <thead
                              style={{
                                backgroundColor:
                                  'rgba(189, 195, 199, 0.10)',
                                color: '#4D4D4D',
                              }}
                            >
                              <tr
                                style={{
                                  alignContent: 'center',
                                  fontSize: '18px',
                                  fontWeight: '400',
                                  lineHeight: 'normal',
                                }}
                              >
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '27%',
                                    alignContent: 'center',
                                    padding: '12px 12px',
                                  }}
                                >
                                  Name
                                </th>
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '20%',
                                    alignContent: 'center',
                                  }}
                                >
                                  Created By
                                </th>
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '15%',
                                    textAlign: 'center',
                                    alignContent: 'center',
                                  }}
                                >
                                  Created On
                                </th>
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '15%',
                                    textAlign: 'center',
                                    alignContent: 'center',
                                  }}
                                >
                                  Status
                                </th>
                                <th
                                  className="table-header-text"
                                  style={{
                                    width: '20%',
                                    textAlign: 'center',
                                    alignContent: 'center',
                                  }}
                                >
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {this.getFilteredGoalsArray().map(
                                (goal, index) => {
                                  const rowCursor = canClickRow
                                    ? 'pointer'
                                    : 'default';
                                  return (
                                    <tr
                                      className="table-row"
                                      key={goal.id || index}
                                      style={{
                                        cursor: rowCursor,
                                        alignContent: 'center',
                                        height: '65px',
                                        verticalAlign: 'middle',
                                      }}
                                    >
                                      {/* <td
                                      className="title-row table-header-text"
                                      onClick={canClickRow ? () => this.handleEdit(goal) : undefined}
                                      style={{ width: "27%", verticalAlign: "middle" }}
                                    > */}
                                      <td
                                        className="title-row table-header-text"
                                        onClick={
                                          canClickRow
                                            ? () =>
                                                this.handleEdit(goal)
                                            : undefined
                                        }
                                        style={{
                                          width: '27%',
                                          verticalAlign: 'middle',
                                        }}
                                      >
                                        <Tooltip
                                          content={goal.title}
                                          position="top"
                                        >
                                          <span className="ellipsis-text">
                                            {goal.title}
                                          </span>
                                        </Tooltip>
                                      </td>
                                      <td
                                        className="table-header-text"
                                        style={{
                                          verticalAlign: 'middle',
                                        }}
                                      >
                                        <Flexbox
                                          flexDirection="row"
                                          flexGrow={0}
                                          className="bp3-cursor-pointer table-header-text"
                                        >
                                          <Flexbox
                                            flexDirection="row"
                                            alignItems="center"
                                          >
                                            <Avatar
                                              src={
                                                goal?.user
                                                  ?.imageFileName
                                              }
                                              size={22}
                                              round={true}
                                              name={
                                                goal.user.fullName
                                              }
                                              className="push-5-r"
                                            />
                                          </Flexbox>
                                          <Flexbox
                                            className="table-header-text"
                                            flexDirection="row"
                                            alignItems="center"
                                          >
                                            <span className="table-header-text">
                                              {goal?.user?.fullName}
                                            </span>
                                          </Flexbox>
                                        </Flexbox>
                                      </td>
                                      <td
                                        className="table-header-text"
                                        style={{
                                          textAlign: 'center',
                                          verticalAlign: 'middle',
                                        }}
                                      >
                                        {new Date(
                                          goal.created_at
                                        ).toLocaleDateString(
                                          'en-US',
                                          {
                                            year: 'numeric',
                                            month: 'short',
                                            day: '2-digit',
                                          }
                                        )}
                                      </td>
                                      <td
                                        style={{
                                          textAlign: 'center',
                                          verticalAlign: 'middle',
                                        }}
                                        className="status-row table-header-text"
                                      >
                                        <span
                                          style={{
                                            border:
                                              '1px solid #D99B00',
                                            textAlign: 'center',
                                            color: '#D99B00',
                                          }}
                                        >
                                          Archived
                                        </span>
                                      </td>
                                      <td
                                        style={{
                                          textAlign: 'center',
                                          verticalAlign: 'middle',
                                        }}
                                        className="status-row table-header-text"
                                      >
                                        <Flexbox
                                          flexDirection="row"
                                          justifyContent="center"
                                        >
                                          {!isReadOnly &&
                                            canEdit &&
                                            goal.status !== 2 && (
                                              <Button
                                                icon={
                                                  <svg
                                                    width="21"
                                                    height="21"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                    <path
                                                      d="M7.63704 21.2615H2.51904L2.69554 16.1885L16.1595 3.1285C16.4168 2.87888 16.761 2.73903 17.1194 2.73847C17.4779 2.73791 17.8225 2.87669 18.0805 3.1255L21.059 5.9975C21.1916 6.12538 21.2973 6.27848 21.3698 6.44778C21.4424 6.61708 21.4803 6.79918 21.4815 6.98337C21.4827 7.16756 21.447 7.35012 21.3766 7.52033C21.3062 7.69053 21.2025 7.84495 21.0715 7.9745L7.63704 21.2615ZM3.47254 20.3385H7.25854L20.4235 7.3195C20.4672 7.27634 20.5018 7.22488 20.5253 7.16815C20.5488 7.11142 20.5607 7.05056 20.5603 6.98916C20.56 6.92776 20.5473 6.86705 20.5231 6.81061C20.499 6.75416 20.4637 6.70313 20.4195 6.6605L17.441 3.7885C17.3552 3.70538 17.2403 3.65891 17.1208 3.65891C17.0013 3.65891 16.8864 3.70538 16.8005 3.7885L3.60054 16.5885L3.47254 20.3385Z"
                                                      fill="#707070"
                                                    />
                                                    <path
                                                      d="M18.8589 9.5245L14.4719 5.21L15.2029 4.491L19.5899 8.8055L18.8589 9.5245Z"
                                                      fill="#707070"
                                                    />
                                                  </svg>
                                                }
                                                minimal
                                                onClick={() =>
                                                  this.handleEdit(
                                                    goal
                                                  )
                                                }
                                                className="no-hover-btn"
                                              />
                                            )}
                                        </Flexbox>
                                      </td>
                                    </tr>
                                  );
                                }
                              )}
                            </tbody>
                          </table>
                        ) : (
                          <div className="push-30-t">
                            <BlankPage
                              title={'No Archived Goal Cycle Found'}
                              description={`You don't have any archived goal cycles.`}
                            />
                          </div>
                        )}
                      </div>
                    }
                  />
                </Tabs>
              </>
            )}
          </>
        ) : (
          <Flexbox
            flexGrow={1}
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            style={{ marginTop: '230px' }}
          >
            <img
              style={{ height: '35px', width: '35px' }}
              src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif"
              alt="Please wait"
            />
          </Flexbox>
        )}
      </Flexbox>
    );
  }
}

GoalsFormList.defaultProps = {
  goals: [],
};

GoalsFormList.propTypes = {
  goals: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onAddGoal: PropTypes.func,
  loading: PropTypes.bool,
  getAllGoals: PropTypes.func,
  CurrentUserStore: PropTypes.object,
};
